import { Button, Icon, Stack, theme, Typography } from "@hiver/hiver-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import SVGInline from "react-svg-inline";
import { showMessage } from "../../actions";
import { useGetSmConversations } from "../../hooks/useGetSmConversations";
import leftArrowIcon from "../../images/back.svg";
import FilterIcon from "../../images/filter.svg";
import { currentSharedMailBoxIdSelector } from "../../selectors";
import Header from "../Header";
import { ConversationFilterPanel } from "./ConversationFilter/ConversationFilterPanel";
import { ConversationTable } from "./ConversationTable";
import { PromptEditor } from "./PromptEditor";
import { getDefaultSmCoversationsPayload } from "./utils";
import { useTestAiPrompt } from "../../hooks/useTestAiPrompt";
import FilterPopoverGuider from "./FilterPopoverGuider";
import { sendTrackingEvent } from "../../helpers";

const ComponentLabel = ({ heading, subheading, isRequired = false }) => {
  return (
    <Stack>
      <Typography
        variant="h3"
        sx={{
          "&::after": {
            color: theme.palette.red.primary,
            content: isRequired ? '" *"' : '""',
            display: "inline",
          },
        }}
      >
        {heading}
      </Typography>
      <Typography variant="body2" color="gray.gray3" sx={{ mb: 1 }}>
        {subheading}
      </Typography>
    </Stack>
  );
};

export const AiPlayground = ({
  headline,
  prompt,
  onPromtUpdate,
  onBackClicked,
  gainsightProps = {},
}) => {
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [conversationsPayload, setConversationsPayload] = useState(
    getDefaultSmCoversationsPayload()
  );
  const [isTestAllLoading, setIsTestAllLoading] = useState(false);
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const smId = useSelector(currentSharedMailBoxIdSelector);
  const {
    data: conversationsData,
    isLoading: isConversationsLoading,
    error: conversationsError,
    refetch: refetchConversations,
  } = useGetSmConversations({ smId, payload: conversationsPayload });

  const conversations = conversationsData?.parsedBody?.data ?? [];

  const { mutateAsync: testAiPromptMutation } = useTestAiPrompt();
  const [testResults, setTestResults] = useState({});

  const setConversationTestResult = ({
    conversationId,
    result,
    isLoading = false,
  }) => {
    setTestResults((prevState) => {
      const newState = prevState?.[conversationId] ?? {};
      newState.isLoading = isLoading;
      if (result !== undefined) newState.result = result;
      return {
        ...prevState,
        [conversationId]: newState,
      };
    });
  };

  const getTestResults = async (conversationIds = [], showSnackbar = true) => {
    await Promise.allSettled(
      conversationIds.map(async (id) => {
        setConversationTestResult({ conversationId: id, isLoading: true });
        try {
          const res = await testAiPromptMutation({
            conversationId: id,
            aiPrompt: prompt,
          });
          const result = res?.data?.extracted_data || "No result found";
          setConversationTestResult({ conversationId: id, result });
          if (showSnackbar) {
            showMessage({
              message: "Prompt test result generated successfully",
            });
          }
        } catch (e) {
          setConversationTestResult({
            conversationId: id,
            result: "Something went wrong! Please try again.",
          });
          if (showSnackbar) {
            showMessage({
              message: "Error generating test result",
              type: "error",
            });
          }
        } finally {
          setConversationTestResult({ conversationId: id, isLoading: false });
        }
      })
    );
  };

  const onTestAllClicked = async () => {
    const conversationIds = conversations.map(
      (conversation) => conversation.gmailunit_id
    );

    setIsTestAllLoading(true);
    await getTestResults(conversationIds, false);
    setIsTestAllLoading(false);
    showMessage({
      message: "Test results generated successfully",
    });
    sendTrackingEvent("AI_CF_Test_page_testall", { ...gainsightProps });
  };

  const getSmConversations = (customPayload) => {
    const payload = customPayload || getDefaultSmCoversationsPayload();
    setConversationsPayload(payload);
    refetchConversations();
  };

  const isTestAllDisabled = useMemo(() => {
    const isAnyConversationTestLoading = Object.keys(testResults).find(
      (conversationId) => testResults[conversationId]?.isLoading
    );

    return (
      isConversationsLoading ||
      isTestAllLoading ||
      isAnyConversationTestLoading ||
      conversations.length <= 0
    );
  }, [isConversationsLoading, isTestAllLoading, testResults, conversations]);

  useEffect(() => {
    if (conversationsError) {
      showMessage({
        message: "Error fetching conversation details",
        type: "error",
      });
    }
  }, [conversationsError]);

  return (
    <Stack
      sx={{
        marginLeft: 5,
        width: "100%",
        height: "100vh",
        "& .backButton": { marginLeft: 0 },
      }}
    >
      <Header
        headLine={headline}
        smSubsection={true}
        image={leftArrowIcon}
        svgClass="backButton"
        onImageClick={onBackClicked}
      />
      <ComponentLabel
        heading="Prompt"
        subheading="Guide the AI on what information to extract from the email subject and
        body"
        isRequired={true}
      />
      <PromptEditor
        prompt={prompt}
        onPromtUpdate={onPromtUpdate}
        disabled={isTestAllDisabled}
        focused={isPromptFocused}
        setFocused={setIsPromptFocused}
      />
      <Stack
        sx={{ flexDirection: "row", alignItems: "center", gap: "10px", mt: 5 }}
      >
        <ComponentLabel
          heading="Recent Conversations"
          subheading="Apply filters to test prompt only on relevant conversations"
        />
        <Button
          color="tertiary"
          size="medium"
          sx={{ ml: "auto" }}
          onClick={onTestAllClicked}
          disabled={isTestAllDisabled || isPromptFocused}
        >
          {isTestAllLoading ? "Testing all..." : "Test all"}
        </Button>
        <Button
          id="conversation-filter-button"
          sx={{
            "& .MuiButton-startIcon": {
              marginRight: "4px",
            },
          }}
          startIcon={
            <Icon sx={{ width: "10px", height: "24px" }}>
              <SVGInline svg={FilterIcon} fill="none" />
            </Icon>
          }
          variant="outlined"
          color="tertiary"
          size="medium"
          onClick={() => setOpenFilterPanel(true)}
        >
          Filter
        </Button>
        <FilterPopoverGuider />
      </Stack>
      <ConversationTable
        testResults={testResults}
        onTestClicked={(conversationId) => getTestResults([conversationId])}
        conversations={conversations}
        isLoading={isConversationsLoading}
        disableAllTestButton={isPromptFocused}
        gainsightProps={gainsightProps}
      />
      <ConversationFilterPanel
        open={openFilterPanel}
        onClose={() => setOpenFilterPanel(false)}
        onApply={getSmConversations}
        gainsightProps={gainsightProps}
      />
    </Stack>
  );
};
